import { fn } from '@ember/helper';
import { or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked, cached } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import {
  setSingleValueForTasField,
  getSingleValueForTasField,
  buildValidationSchemaForProgramTemplateFields,
  buildValidationSchemaForCustomFields,
  buildCustomFieldsFormModelForValidation,
  copyFieldsAndUpdatePerProgramTemplate,
  validateAndCollectErrors,
} from '../../../../utils/tuition-assistance/fields.ts';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import errorsForField from '../../../../helpers/errors-for-field.ts';
import inputmask from '../../../../modifiers/inputmask.ts';
import { getInputmaskAlias, getInputmaskUnmaskedValue } from '../../../../utils/inputmask.ts';
import TuitionAssistanceActionButtons from '../../action-buttons.gts';
import TuitionAssistanceFormsCoursesGradeSelectOptions from './grade-select-options.gts';
import TuitionAssistanceFormsCoursesHeldParticipantWarning from './held-participant-warning.gts';
import TuitionAssistanceFormsCustomFieldsForProvince from '../custom-fields-for-province.gts';
import TuitionAssistanceFormsUploadAttachments from '../upload-attachments.gts';
import TuitionAssistanceProgramFormField from '../../program-form-field.gts';
import type {
  CustomFieldSignature,
  CourseFormField,
} from '../../../../types/tuition-assistance.ts';
import type { FieldName } from '../../../../utils/tuition-assistance/fields.ts';
import type { IntlService } from 'ember-intl';
import type { TasFieldsOrCustomFields } from '../../../../types/tuition-assistance.ts';
import type RouterService from '@ember/routing/router';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type Owner from '@ember/owner';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/data/resources/tas-program-template.type';
import type { TASCourseModelFieldsSignature } from 'tio-data/data/resources/tas-course.type';

const number = Number;

export interface CourseDetailsSignature {
  Args: {
    course: TasCourseModel;
    isAdminView?: boolean;
    cancel?: () => void;
    saveAndNext?: (
      fieldsCopy: TasCourseModel['fields'],
      customFieldsCopy: TasCourseModel['customFields'],
      isFormValid: boolean
    ) => void;
    saveForLater?: (
      fieldsCopy: TasCourseModel['fields'],
      customFieldsCopy: TasCourseModel['customFields']
    ) => void;
    showActionButtons: boolean;
    didUpdate?: (field: TasFieldsOrCustomFields, key: string) => void;
    saveTaskIsRunning?: boolean;
    sectionValid?: (arg0: boolean) => void;
    hasSubmitted: boolean;
  };
}

type ClassificationOptionType = { label: string; key: string };

export default class CourseDetailsComponent extends Component<CourseDetailsSignature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;

  customFieldsProvince = 'COURSE';
  // @ts-expect-error: not sure about this one
  courseFieldsCopy: TasCourseModel['fields'] = [];
  @tracked customFieldsCopy: TasCourseModel['customFields'] = [];
  @tracked selectedClassification: string = '';
  @tracked uploadClassificationField = 'COURSE_APPROVAL_DOCUMENTS';
  originalCourseFields: TasCourseModel['fields'];

  constructor(owner: Owner, args: CourseDetailsSignature['Args']) {
    super(owner, args);
    this.courseFieldsCopy = trackedObject(this.args.course.fields);
    const programTemplateCustomFields = this.programTemplate.courseCustomFields || [];
    this.customFieldsCopy = copyFieldsAndUpdatePerProgramTemplate(
      this.args.course.customFields,
      programTemplateCustomFields
    );
    if (this.args.sectionValid) {
      this.args.sectionValid?.(this.isFormValid);
    }
    this.originalCourseFields = this.args.course.fields || {};
  }

  getSingleValueForTasField = getSingleValueForTasField;

  get programTemplate() {
    return this.args.course?.tasApplication?.tasProgramInstance?.tasProgramTemplate || {};
  }

  get classificationOptions(): ClassificationOptionType[] {
    const isInPreAttendState = this.args.course.tasApplication.inPreAttendState;

    const options: ClassificationOptionType[] = [
      {
        label: 'Pre-Approval Documents',
        key: 'COURSE_APPROVAL_DOCUMENTS',
      },
      {
        label: 'Completion Documents',
        key: 'COMPLETION_DOCUMENTS',
      },
    ];

    return isInPreAttendState
      ? options.filter((option) => option.key === 'COURSE_APPROVAL_DOCUMENTS')
      : options;
  }

  get showHeldParticipantWarning() {
    return !!this.args.course.tasApplication?.tasProgramInstance?.employee?.tasParticipant?.isHeld;
  }

  get courseFormFields(): CourseFormField[] {
    const numberTypeError = 'Value must be a number';
    return [
      { name: 'COURSE_NAME', mask: null, type: null, rules: { type: 'string' } },
      { name: 'COURSE_NUMBER', mask: null, type: null, rules: { type: 'string' } },
      { name: 'COURSE_DESCRIPTION', mask: null, type: null, rules: { type: 'string' } },
      {
        name: 'COURSE_CREDIT',
        mask: null,
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_TUITION',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_BOOKS',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_LABS',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_SOFTWARE',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_FEES',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
      },
    ];
  }

  get courseExpenses() {
    const a = this.formModel;
    const cost = [
      a.COURSE_TUITION || 0,
      a.COURSE_BOOKS || 0,
      a.COURSE_LABS || 0,
      a.COURSE_SOFTWARE || 0,
      a.COURSE_FEES || 0,
    ];

    const reduced = cost.reduce((a, b) => {
      return a + b;
    }, 0);

    return reduced;
  }

  get reimbursementTotalByCredit() {
    return (
      (this.programTemplate.ratePerCredit ?? 0) *
      ((getSingleValueForTasField('COURSE_CREDIT', this.courseFieldsCopy) as number) ?? 0)
    );
  }

  get courseTotal() {
    return this.programTemplate.calculateTotalByCredit
      ? this.reimbursementTotalByCredit
      : this.courseExpenses;
  }

  get isFormValid() {
    if (this.requireAttachmentUploads) {
      return !this.formValidationErrors.length && this.hasAttachments;
    }
    return !this.formValidationErrors.length;
  }

  get formModel() {
    const customFieldsFormModelForProvince = buildCustomFieldsFormModelForValidation(
      this.customFieldsForProvince
    );
    return {
      COURSE_NAME: getSingleValueForTasField('COURSE_NAME', this.courseFieldsCopy),
      COURSE_NUMBER: getSingleValueForTasField('COURSE_NUMBER', this.courseFieldsCopy),
      COURSE_DESCRIPTION: getSingleValueForTasField('COURSE_DESCRIPTION', this.courseFieldsCopy),
      COURSE_CREDIT: getSingleValueForTasField('COURSE_CREDIT', this.courseFieldsCopy),
      COURSE_TUITION: getSingleValueForTasField('COURSE_TUITION', this.courseFieldsCopy),
      COURSE_BOOKS: getSingleValueForTasField('COURSE_BOOKS', this.courseFieldsCopy),
      COURSE_LABS: getSingleValueForTasField('COURSE_LABS', this.courseFieldsCopy),
      COURSE_SOFTWARE: getSingleValueForTasField('COURSE_SOFTWARE', this.courseFieldsCopy),
      COURSE_FEES: getSingleValueForTasField('COURSE_FEES', this.courseFieldsCopy),
      COURSE_GRADE: getSingleValueForTasField('COURSE_GRADE', this.courseFieldsCopy),
      ...customFieldsFormModelForProvince,
    };
  }

  @cached
  get dynamicValidationSchema() {
    /** COURSE_GRADE is purposely left out of this schema because we don't want to require it in this step */
    const fields = this.courseFormFields;
    return buildValidationSchemaForProgramTemplateFields(fields, this.programTemplate);
  }

  @cached
  get dynamicValidationSchemaForCustomFields() {
    return buildValidationSchemaForCustomFields(this.customFieldsForProvince);
  }

  get customFieldsForProvince() {
    return this.customFieldsCopy.filter(
      (field: CustomFieldSignature) => field.province === this.customFieldsProvince
    );
  }

  get formValidationErrors() {
    return validateAndCollectErrors(
      this.formModel,
      this.dynamicValidationSchema,
      this.dynamicValidationSchemaForCustomFields
    );
  }

  get requireAttachmentUploads() {
    return this.programTemplate.requireAttachmentsForCourseApproval;
  }

  get attachmentUploadsVisible() {
    const attachmentsVisibleForAdmin =
      this.programTemplate.attachmentsForCourseApprovalVisible ||
      this.programTemplate.attachmentsForCourseCompletionVisible;
    return this.args.isAdminView
      ? attachmentsVisibleForAdmin
      : this.programTemplate.attachmentsForCourseApprovalVisible;
  }

  get hasAttachments() {
    return !!this.args.course?.approvalAssets?.length;
  }

  setApprovedCourseTotalBeforeSave() {
    setSingleValueForTasField('APPROVED_COURSE_TOTAL', this.courseTotal, this.courseFieldsCopy);
  }

  setUploadClassification = (classification: string) => {
    this.selectedClassification = classification;
    this.uploadClassificationField = classification || 'COURSE_APPROVAL_DOCUMENTS';
  };

  updateValueForField = (
    fieldName: FieldName,
    type: string | null | undefined,
    raw: number | string | string[],
    event?: Event | undefined
  ) => {
    let valueToSet: string | number = Array.isArray(raw) ? (raw[0] ?? '') : raw;

    if (getInputmaskAlias(event) === 'currency') {
      valueToSet = Number(getInputmaskUnmaskedValue(event)) * 100;
    }
    if (type === 'number') {
      valueToSet = Math.round(Number(valueToSet));
    }
    // @ts-expect-error: fieldName
    setSingleValueForTasField(fieldName, valueToSet, this.courseFieldsCopy);
    this.setApprovedCourseTotalBeforeSave();
    this.didUpdateFields(this.courseFieldsCopy, 'tasCourseFields');
  };

  didUpdateCustomFields = (updated: CustomFieldSignature[] = []) => {
    this.customFieldsCopy = updated;
    this.didUpdateFields(this.customFieldsCopy, 'tasCourseCustomFields');
  };

  didUpdateFields = (
    fields: TasCourseModel['fields'] | TasCourseModel['customFields'],
    key: string
  ) => {
    this.args.didUpdate?.(fields, key);
    this.args.sectionValid?.(this.isFormValid);
  };

  selectedKeyForField = (name: keyof TASProgramTemplateModelFieldsSignature) => {
    return getSingleValueForTasField(
      name as keyof TASCourseModelFieldsSignature,
      this.courseFieldsCopy
    ) as string | undefined;
  };

  <template>
    <form class="flex flex-wrap">
      <div class="grid md:grid-cols-2 mt-5 md:mt-10 gap-4 w-full md:w-2/3">
        {{#each this.courseFormFields as |formField|}}
          <TuitionAssistanceProgramFormField
            @name={{formField.name}}
            @programTemplate={{@course.tasApplication.tasProgramInstance.tasProgramTemplate}}
            as |field|
          >
            <div class="my-2 col-span-1">
              {{#if field.isFieldOptionPicklist}}
                <Select
                  data-test={{field.name}}
                  id={{field.inputId}}
                  @items={{field.fieldOptions}}
                  @selected={{getSingleValueForTasField field.name this.originalCourseFields}}
                  @onChange={{fn this.updateValueForField field.name formField.type}}
                  @errors={{if
                    field.isTouched
                    (errorsForField field.name schemaErrors=this.formValidationErrors)
                  }}
                  {{on "focusout" field.markTouched}}
                />
              {{else if (eq formField.mask "currency")}}
                <Input
                  data-test={{field.name}}
                  id={{field.inputId}}
                  {{! initial value is the original field value, not the copy, because of inputmask on FormInput being funny }}
                  @value={{divide
                    (or (number (getSingleValueForTasField field.name this.originalCourseFields)) 0)
                    100
                  }}
                  @onChange={{fn this.updateValueForField field.name formField.type}}
                  @errors={{if
                    field.isTouched
                    (errorsForField field.name schemaErrors=this.formValidationErrors)
                  }}
                  {{on "blur" field.markTouched}}
                  {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
                />
              {{else}}
                <Input
                  data-test={{field.name}}
                  id={{field.inputId}}
                  @value={{getSingleValueForTasField field.name this.courseFieldsCopy}}
                  @onChange={{fn this.updateValueForField field.name formField.type}}
                  @errors={{if
                    field.isTouched
                    (errorsForField field.name schemaErrors=this.formValidationErrors)
                  }}
                  {{on "blur" field.markTouched}}
                />
              {{/if}}
            </div>
          </TuitionAssistanceProgramFormField>
        {{/each}}
        <TuitionAssistanceFormsCustomFieldsForProvince
          @province={{this.customFieldsProvince}}
          @customFields={{this.customFieldsCopy}}
          @programTemplate={{this.programTemplate}}
          @didUpdateFields={{this.didUpdateCustomFields}}
          @formValidationErrors={{this.formValidationErrors}}
          @fieldContainerClass="col-span-1"
          @hasSubmitted={{@hasSubmitted}}
        />
      </div>

      <div class="md:row-span-4 mt-5 md:mt-10 md:w-1/3">
        <div class="mx-8 sm:max-w-72">
          <TuitionAssistanceFormsCoursesGradeSelectOptions
            @programTemplate={{@course.tasApplication.tasProgramInstance.tasProgramTemplate}}
            @updateCourseGrade={{fn this.updateValueForField "COURSE_GRADE" "string"}}
            @formValidationErrors={{this.formValidationErrors}}
            @fieldsCopy={{this.courseFieldsCopy}}
          />
        </div>
        {{#if this.attachmentUploadsVisible}}
          <div class="mt-4 mx-8 sm:max-w-72">
            {{#if @isAdminView}}
              <div class="my-4">
                <label
                  for="classification-type"
                  class="tio-text-sm-semibold tio-fg-secondary block mb-1"
                >
                  {{t "tuition_assistance.program_details.courses.document_type"}}
                </label>
                <Select
                  data-test-classification-type
                  @items={{this.classificationOptions}}
                  @selected={{this.selectedClassification}}
                  @onChange={{this.setUploadClassification}}
                  @name="classification-type"
                >
                  <:option as |opt|>{{opt.label}}</:option>
                </Select>
              </div>
            {{/if}}
            <TuitionAssistanceFormsUploadAttachments
              @programTemplate={{@course.tasApplication.tasProgramInstance.tasProgramTemplate}}
              @classification={{this.uploadClassificationField}}
              @attachable={{@course}}
              @attachableType="tasCourse"
              @uploadRequired={{this.requireAttachmentUploads}}
              @disabled={{false}}
            />
          </div>
        {{/if}}
      </div>
    </form>

    {{#if this.showHeldParticipantWarning}}
      <TuitionAssistanceFormsCoursesHeldParticipantWarning class="my-4 mx-auto max-w-md" />
    {{/if}}

    {{#if @showActionButtons}}
      <TuitionAssistanceActionButtons
        @cancel={{@cancel}}
        {{! @glint-expect-error}}
        @saveAndNext={{@saveAndNext}}
        {{! @glint-expect-error}}
        @saveForLater={{@saveForLater}}
        @fieldsCopy={{this.courseFieldsCopy}}
        @customFieldsCopy={{this.customFieldsCopy}}
        @isFormValid={{this.isFormValid}}
        @saveTaskIsRunning={{@saveTaskIsRunning}}
        class="my-8"
      />
    {{/if}}
  </template>
}
